/**
 * 카카오 모먼트 이미지 유효성 검증 유틸.
 *
 * 타입/슬롯별 허용 사이즈 배열과 공통 검증 함수를 제공한다.
 * 파일 선택(input file) 시와 라이브러리 URL 선택 시 모두 사용한다.
 */

/** [width, height] 픽셀 쌍 */
export type AllowedSize = [number, number];

/**
 * 타입/슬롯별 허용 이미지 사이즈 목록.
 * 카카오 모먼트 이미지 정책 권장 사이즈 기준.
 *
 * 공통 제약: JPG/PNG, 가로 80px 이상, 10MB 이하
 */
export const MOMENT_IMAGE_ALLOWED_SIZES = {
  /** 와이드 이미지형: 800×600 (4:3) */
  WIDE_MESSAGE: [[800, 600]] as AllowedSize[],
  /** 와이드 리스트 첫 번째(헤더): 800×400 (2:1) */
  WIDE_LIST_HEADER: [[800, 400]] as AllowedSize[],
  /** 와이드 리스트 2~5번째(아이템): 800×800 (1:1) */
  WIDE_LIST_ITEM: [[800, 800]] as AllowedSize[],
  /** 캐러셀 피드 카드: 800×400 (2:1) 또는 800×600 (4:3) */
  CAROUSEL_FEED: [[800, 400], [800, 600]] as AllowedSize[],
  /** 캐러셀 커머스 카드/인트로: 800×400, 800×800, 800×600 */
  CAROUSEL_COMMERCE: [[800, 400], [800, 800], [800, 600]] as AllowedSize[],
} as const;

const MAX_MOMENT_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB (카카오 정책)
const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png"];

/**
 * 파일 크기 + MIME 타입을 동기적으로 체크.
 * 통과 시 null, 실패 시 사용자에게 노출할 에러 메시지 반환.
 */
export function validateImageFileMeta(file: File): string | null {
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return "JPG 또는 PNG 형식의 이미지만 업로드할 수 있습니다.";
  }
  if (file.size > MAX_MOMENT_IMAGE_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return `이미지 파일 크기는 10MB 이하여야 합니다. (현재: ${sizeMB}MB)`;
  }
  return null;
}

/**
 * 이미지 dimension이 허용 사이즈 배열 중 하나와 일치하는지 비동기 체크.
 *
 * File 또는 URL(라이브러리 선택 시 기존 이미지 URL) 모두 지원.
 * URL의 경우 img_width/img_height가 null인 기존 이미지에 대한 fallback으로 사용한다.
 *
 * @param source   검증할 File 또는 이미지 URL
 * @param allowed  허용 사이즈 배열 (e.g. [[800,400],[800,600]])
 * @returns null이면 통과, string이면 에러 메시지
 */
export function validateImageDimension(
  source: File | string,
  allowed: AllowedSize[]
): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new window.Image();
    const isFile = source instanceof File;
    const objectUrl = isFile ? URL.createObjectURL(source) : source;

    img.onload = () => {
      if (isFile) URL.revokeObjectURL(objectUrl);
      const matched = allowed.some(([w, h]) => img.width === w && img.height === h);
      if (!matched) {
        const sizesStr = allowed.map(([w, h]) => `${w}×${h}`).join(" 또는 ");
        resolve(`이미지 크기는 ${sizesStr}px여야 합니다. (현재: ${img.width}×${img.height}px)`);
      } else {
        resolve(null);
      }
    };
    img.onerror = () => {
      if (isFile) URL.revokeObjectURL(objectUrl);
      resolve("이미지를 읽을 수 없습니다.");
    };
    img.src = objectUrl;
  });
}

/**
 * 허용 사이즈 배열을 힌트 문자열로 변환.
 * e.g. [[800,400],[800,600]] → "800×400px 또는 800×600px"
 */
export function formatAllowedSizes(allowed: AllowedSize[]): string {
  return allowed.map(([w, h]) => `${w}×${h}px`).join(" 또는 ");
}
